import DOMPurify from "dompurify";
import { ImageOff, Moon, Sun } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from "react";

import { useDarkTheme } from "../../../common/useDarkTheme.js";

/** Was in einer Mail nichts verloren hat: ausführbarer Inhalt, Formulare, eigene Rahmen. */
const FORBIDDEN_TAGS = ["script", "iframe", "frame", "frameset", "object", "embed", "applet", "form", "input", "button", "textarea", "select", "meta", "base", "link"];
const REMOTE_REFERENCE = /(?:\b(?:src|background|poster)\s*=\s*["']?\s*(?:https?:)?\/\/)|(?:url\(\s*["']?\s*(?:https?:)?\/\/)/i;
/** Mails, die selbst ein dunkles Aussehen mitbringen, färben wir nicht um. */
const OWN_DARK_STYLES = /prefers-color-scheme\s*:\s*dark|color-scheme\s*:[^;"']*dark/i;

/**
 * Dunkle Darstellung wie bei Gmail und Apple Mail: Die ganze Mail wird umgekehrt und im
 * Farbton zurückgedreht, sodass Weiß dunkel und Schwarz hell wird, Farben aber ihren Ton
 * behalten. Bilder und Videos werden ein zweites Mal umgekehrt und sehen dadurch aus wie
 * im Original.
 */
const DARK_STYLES = "html{filter:invert(1) hue-rotate(180deg)}html,body{background:#e3e1dc!important}img,video,picture,svg,[style*=\"background-image\"],[background]{filter:invert(1) hue-rotate(180deg)}";

/**
 * Bereinigt eine HTML-Mail und baut daraus ein eigenständiges Dokument.
 *
 * Zwei Riegel: DOMPurify entfernt Skripte und Formulare, und die Content Security Policy
 * im Dokument lässt nur eingebettete Bilder zu. Externe Bilder und Schriften lädt der
 * Browser erst, wenn `allowRemote` gesetzt ist; sonst verriete ein Zählpixel beim Öffnen,
 * wer die Mail wann gelesen hat.
 */
export function mailDocument(html: string, options: { allowRemote: boolean; dark?: boolean }): { document: string; hasRemoteContent: boolean; canDarken: boolean } {
  const clean = DOMPurify.sanitize(html, { FORBID_TAGS: FORBIDDEN_TAGS, FORCE_BODY: true, ADD_ATTR: ["target"] });
  const remote = options.allowRemote ? " https: http:" : "";
  const policy = `default-src 'none'; style-src 'unsafe-inline'${remote}; img-src data:${remote}; font-src data:${remote}; media-src data:${remote}`;
  const canDarken = !OWN_DARK_STYLES.test(clean);
  const dark = options.dark === true && canDarken ? DARK_STYLES : "";
  return {
    hasRemoteContent: REMOTE_REFERENCE.test(clean),
    canDarken,
    document: `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${policy}"><meta name="referrer" content="no-referrer"><base target="_blank"><style>html,body{margin:0;padding:0;background:#fff;color:#111;font:15px/1.6 system-ui,sans-serif;overflow-wrap:anywhere}html{overflow:hidden}body{padding:16px}img{max-width:100%;height:auto}table{max-width:100%}${dark}</style></head><body>${clean}</body></html>`,
  };
}

/** Eine HTML-Mail in einem abgeschotteten Rahmen, so hoch wie ihr Inhalt. */
export function MailHtmlView({ html }: { html: string }): JSX.Element {
  const darkTheme = useDarkTheme();
  const [allowRemote, setAllowRemote] = useState(false);
  const [light, setLight] = useState(false);
  const dark = darkTheme && !light;
  const { document, hasRemoteContent, canDarken } = useMemo(() => mailDocument(html, { allowRemote, dark }), [html, allowRemote, dark]);
  const frame = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(240);

  // Ohne allow-scripts läuft im Rahmen nichts; allow-same-origin brauchen wir nur, um
  // von außen die Höhe des Inhalts zu messen. Bilder laden oft erst nach dem Ereignis
  // `load` fertig, deshalb misst ein ResizeObserver weiter mit.
  const observer = useRef<ResizeObserver | null>(null);
  const attach = useCallback(() => {
    observer.current?.disconnect();
    const doc = frame.current?.contentDocument;
    if (!doc?.body) return;
    const measure = () => setHeight(Math.max(120, Math.ceil(doc.documentElement.getBoundingClientRect().height), doc.body.scrollHeight));
    measure();
    observer.current = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer.current?.observe(doc.body);
  }, []);
  useEffect(() => () => observer.current?.disconnect(), []);

  useEffect(() => {
    setAllowRemote(false);
    setLight(false);
  }, [html]);

  const showRemoteHint = hasRemoteContent && !allowRemote;
  return (
    <div className="mt-7">
      {showRemoteHint || (darkTheme && canDarken) ? (
        <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-ink-muted">
          {showRemoteHint ? (
            <p className="flex min-w-0 flex-1 flex-wrap items-center gap-2 rounded-xl border border-line bg-surface-sunken px-3 py-2">
              <ImageOff className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="min-w-0 flex-1">Externe Bilder sind blockiert, damit der Absender nicht sieht, wann du die Mail liest.</span>
              <button type="button" onClick={() => setAllowRemote(true)} className="rounded-lg border border-line px-2.5 py-1 font-bold text-ink hover:bg-surface">Bilder laden</button>
            </p>
          ) : <span className="flex-1" />}
          {darkTheme && canDarken ? (
            <button type="button" onClick={() => setLight((value) => !value)} className="inline-flex items-center gap-1.5 rounded-lg border border-line px-2.5 py-1.5 font-bold text-ink hover:bg-surface-sunken">
              {light ? <><Moon className="h-3.5 w-3.5" aria-hidden="true" />Dunkel anzeigen</> : <><Sun className="h-3.5 w-3.5" aria-hidden="true" />Hell anzeigen</>}
            </button>
          ) : null}
        </div>
      ) : null}
      {/* Neuer Rahmen bei jeder Änderung: Safari lädt ein geändertes srcdoc sonst nicht immer neu. */}
      <iframe
        key={`${allowRemote}-${dark}`}
        ref={frame}
        title="Inhalt der Nachricht"
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        srcDoc={document}
        onLoad={attach}
        scrolling="no"
        style={{ height }}
        className={`block w-full overflow-hidden rounded-xl border border-line ${dark && canDarken ? "bg-surface-raised" : "bg-white"}`}
      />
    </div>
  );
}
